import CloseIcon from "@assets/svg/CloseIcon";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import FormControl from "@components/FormControl/FormControl";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@components/ui/dialog";
import FormSelect from "@components/FormSelect/FormSelect";
import { colors } from "@utils/theme";
import React, { SetStateAction, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { updateAspiration } from "@endpoints/candidate.service";
import { setAspirationDetails } from "@store/actions/auth.action";
import ClassificationSelector from "@components/ClassificationSelector/ClassificationSelector";
import { contractOptions, positionOptions } from "../sections/TargetSection";

interface AddEditTargetModalProps {
  isEditing: boolean;
  setIsEditing: React.Dispatch<SetStateAction<boolean>>;
  aspirationDetails: any;
}

const AddEditTargetModal = ({
  isEditing,
  setIsEditing,
  aspirationDetails,
}: AddEditTargetModalProps) => {
  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      jobTitle: "",
      industryType: "",
      positionLevel: "",
      location: "",

      salary: {
        min: "",
        max: "",
      },
      company: "",
      contractType: "",
      classification: { main: "", sub: [] as string[] },
    },
  });

  const [isUpdating, setIsUpdating] = useState(false);

  const handleSave = async (formData: any) => {
    try {
      setIsUpdating(true);
      const payload = formData;
      payload._id = aspirationDetails?._id;
      const { success, data } = await updateAspiration(payload);
      if (success) {
        dispatch(setAspirationDetails(data));
      }
    } catch (error) {
      console.error("Error updating aspiration details", error);
    } finally {
      setIsUpdating(false);
      setIsEditing(false);
      reset();
    }
  };

  // Hydrate the edit form whenever the details change
  useEffect(() => {
    if (!aspirationDetails) return;

    reset({
      jobTitle: aspirationDetails.jobTitle ?? "",
      industryType: aspirationDetails.industryType ?? "",
      positionLevel: aspirationDetails.positionLevel ?? "",
      location: aspirationDetails.location ?? "",
      salary: {
        min: aspirationDetails.salary?.min?.toString() ?? "",
        max: aspirationDetails.salary?.max?.toString() ?? "",
      },
      company: aspirationDetails.company ?? "",
      contractType: aspirationDetails.contractType ?? "",
      classification: {
        main: aspirationDetails.classification?.main ?? "",
        sub: aspirationDetails.classification?.sub ?? [],
      },
    });
  }, [aspirationDetails, reset]);

  return (
    <Dialog open={isEditing} onOpenChange={setIsEditing}>
      <DialogContent showCloseButton={false} className="bg-white !max-w-3xl">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-lg">Your Target Role</span>
            <span className="font-medium text-xs text-light-black">
              Update the details that define the roles you're aiming for.
            </span>
          </div>
          <DialogClose asChild>
            <button
              onClick={() => setIsEditing(false)}
              className="h-max p-2 rounded-md hover:bg-gray-50"
            >
              <CloseIcon className={"w-5 h-5"} />
            </button>
          </DialogClose>
        </div>
        <div className="bg-[#F9FAFB] p-5 rounded-md flex flex-col gap-6">
          <div className="flex items-start gap-5">
            <FormControl
              label="Job Title"
              name="jobTitle"
              placeholder={"e.g. Cybersecurity Engineer"}
              control={control}
              errors={errors}
              rules={{
                required: "This field is required.",
              }}
            />
            <FormControl
              label="Target Industry"
              name="industryType"
              placeholder={"e.g. Information Technology"}
              control={control}
              errors={errors}
              rules={{
                required: "This field is required.",
              }}
            />
          </div>
          <div className="flex items-start gap-5">
            <FormSelect
              label="Target Seniority"
              name="positionLevel"
              placeholder={"Select seniority level"}
              options={positionOptions}
              control={control}
              errors={errors}
              rules={{
                required: "This field is required.",
              }}
            />
            <FormControl
              label="Preferred Location"
              name="location"
              placeholder={"e.g. London, United Kingdom"}
              control={control}
              errors={errors}
              rules={{
                required: "This field is required.",
              }}
            />
          </div>
          <div className="flex items-start gap-5">
            <Controller
              name="classification"
              control={control}
              rules={{
                validate: (value) => !!value?.main || "This field is required.",
              }}
              render={({ field, fieldState }) => (
                <ClassificationSelector
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  placeholder="Select preferred classification"
                />
              )}
            />
          </div>
          <div className="flex items-start gap-5">
            <FormControl
              label="Min Salary"
              name="salary.min"
              placeholder={"e.g. 80,000"}
              control={control}
              errors={errors}
              rules={{
                required: "This field is required.",
              }}
            />
            <FormControl
              label="Max Salary"
              name="salary.max"
              placeholder={"e.g. 100,000"}
              control={control}
              errors={errors}
              rules={{
                required: "This field is required.",
              }}
            />
          </div>
          <div className="flex items-start gap-5">
            <FormControl
              label="Target Company"
              name="company"
              placeholder={"e.g. Google, Microsoft"}
              control={control}
              errors={errors}
              rules={{
                required: "This field is required.",
              }}
            />
            <FormSelect
              label="Target Contract"
              name="contractType"
              placeholder={"Select contract type"}
              options={contractOptions}
              control={control}
              errors={errors}
              rules={{
                required: "This field is required.",
              }}
            />
          </div>
        </div>
        <DialogFooter className="mt-5 gap-3">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="font-semibold text-sm text-slate-700 border py-2 px-5 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit(handleSave)}
            disabled={isUpdating}
            className="font-semibold text-sm bg-primary text-white py-2 px-5 rounded-md flex items-center gap-2 disabled:opacity-60"
          >
            Save Changes
            {isUpdating && <LoadingAnimation color={colors.White} />}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditTargetModal;
